import { ExecStatus, ExecStep } from '../interfaces/execution-plan.interfaces';

/**
 * What a link into a job's page can ask it to show, via `?view=` (EHQ-408).
 * View Logs in the routines history sends `logs`, View Files sends `files`.
 */
export type JobPageView = 'logs' | 'files';

/** The query param that carries a {@link JobPageView}. */
export const JOB_PAGE_VIEW_PARAM = 'view';

/** The view a `?view=` value asks for, or null for a missing or unknown one. */
export function parseJobPageView(value: string | null): JobPageView | null {
  return value === 'logs' || value === 'files' ? value : null;
}

/** Step states whose logs are not complete yet. */
const UNFINISHED_STEP_STATES: ReadonlySet<ExecStatus> = new Set([
  ExecStatus.Pending,
  ExecStatus.Scheduled,
  ExecStatus.Executing,
  ExecStatus.Retry,
]);

/**
 * The step panels to open for `?view=logs`: every failed step, or the last
 * finished step when none failed.
 *
 * Opening every panel fetched logs and files for all steps at once (120
 * requests on a 60-step routine), and a step that had not finished yet loaded
 * empty logs, which stay cached as "(no logs)" until a reload. So an
 * unfinished step is never picked.
 */
export function stepsToOpenForLogs(steps: readonly ExecStep[]): number[] {
  const failed = steps.filter((step) => step.status === ExecStatus.Failed);
  if (failed.length > 0) return failed.map((step) => step.execStepId);
  let last: ExecStep | undefined;
  for (const step of steps) {
    if (UNFINISHED_STEP_STATES.has(step.status)) continue;
    if (!last || step.stepIndex > last.stepIndex) last = step;
  }
  return last ? [last.execStepId] : [];
}
